import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  useExerciseSets,
  useExerciseType,
  useCreateExerciseSet,
  useUpdateExerciseSet,
  useDeleteExerciseSet,
  useUpdateExerciseSetPosition,
} from "./exercise_sets_api";

// SortableExerciseSetRow component for drag-and-drop functionality for weighted exercises
const SortableWeightedSetRow = ({ id, setData, exerciseId }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dragHandleStyle = {
    touchAction: "none",
    cursor: "grab",
  };

  const [reps, setReps] = useState(setData.reps);
  const [weight, setWeight] = useState(setData.weight);
  const [hasChanges, setHasChanges] = useState(false);
  const updateSetMutation = useUpdateExerciseSet();
  const deleteSetMutation = useDeleteExerciseSet();

  const handleRepsChange = (e) => {
    const newReps = parseInt(e.target.value, 10);
    setReps(newReps);
    setHasChanges(newReps !== setData.reps || weight !== setData.weight);
  };

  const handleWeightChange = (e) => {
    const newWeight = parseFloat(e.target.value);
    setWeight(newWeight);
    setHasChanges(reps !== setData.reps || newWeight !== setData.weight);
  };

  const handleUpdate = () => {
    updateSetMutation.mutate(
      {
        exerciseId,
        setId: id,
        reps,
        weight,
      },
      {
        onSuccess: () => {
          setHasChanges(false);
        },
      }
    );
  };

  const handleDelete = () => {
    deleteSetMutation.mutate({
      exerciseId,
      setId: id,
    });
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td className="d-flex align-items-center">
        <div
          className="set-drag-handle"
          {...attributes}
          {...listeners}
          style={dragHandleStyle}
        >
          <i className="bi bi-grip-vertical text-secondary"></i>
        </div>
      </td>
      <td>
        <div className="reps-input-container">
          <input
            type="number"
            min="1"
            className="form-control form-control-sm"
            value={reps}
            onChange={handleRepsChange}
          />
        </div>
      </td>
      <td>
        <div className="weight-input-container">
          <input
            type="number"
            min="0"
            step="0.5"
            className="form-control form-control-sm"
            value={weight}
            onChange={handleWeightChange}
          />
        </div>
      </td>
      <td>
        {hasChanges ? (
          <button className="btn btn-sm btn-primary" onClick={handleUpdate}>
            <i className="bi bi-check"></i>
          </button>
        ) : (
          <button className="btn btn-sm btn-danger" onClick={handleDelete}>
            <i className="bi bi-trash"></i>
          </button>
        )}
      </td>
    </tr>
  );
};

// SortableExerciseSetRow component for timed exercises
const SortableTimedSetRow = ({ id, setData, exerciseId }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dragHandleStyle = {
    touchAction: "none",
    cursor: "grab",
  };

  const [duration, setDuration] = useState(setData.duration || 0);
  const [hasChanges, setHasChanges] = useState(false);
  const updateSetMutation = useUpdateExerciseSet();
  const deleteSetMutation = useDeleteExerciseSet();

  const handleDurationChange = (e) => {
    const newDuration = parseInt(e.target.value, 10);
    setDuration(newDuration);
    setHasChanges(newDuration !== setData.duration);
  };

  const handleUpdate = () => {
    updateSetMutation.mutate(
      {
        exerciseId,
        setId: id,
        duration,
      },
      {
        onSuccess: () => {
          setHasChanges(false);
        },
      }
    );
  };

  const handleDelete = () => {
    deleteSetMutation.mutate({
      exerciseId,
      setId: id,
    });
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td className="d-flex align-items-center">
        <div
          className="set-drag-handle"
          {...attributes}
          {...listeners}
          style={dragHandleStyle}
        >
          <i className="bi bi-grip-vertical text-secondary"></i>
        </div>
      </td>
      <td colSpan="2">
        <div className="duration-input-container">
          <input
            type="number"
            min="1"
            className="form-control form-control-sm"
            value={duration}
            onChange={handleDurationChange}
            placeholder="Duration (seconds)"
          />
        </div>
      </td>
      <td>
        {hasChanges ? (
          <button className="btn btn-sm btn-primary" onClick={handleUpdate}>
            <i className="bi bi-check"></i>
          </button>
        ) : (
          <button className="btn btn-sm btn-danger" onClick={handleDelete}>
            <i className="bi bi-trash"></i>
          </button>
        )}
      </td>
    </tr>
  );
};

// Main ExerciseSets component
const ExerciseSets = ({ exerciseId }) => {
  const {
    data: exerciseSets,
    isLoading: isLoadingSets,
    isError: isErrorSets,
  } = useExerciseSets(exerciseId);

  const {
    data: exerciseTypeData,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useExerciseType(exerciseId);

  const createSetMutation = useCreateExerciseSet();
  const updatePositionMutation = useUpdateExerciseSetPosition();

  const [newSetReps, setNewSetReps] = useState("");
  const [newSetWeight, setNewSetWeight] = useState("");
  const [newDuration, setNewDuration] = useState("");

  const isTimedExercise = exerciseTypeData?.exercise_type === "timed";

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = exerciseSets.findIndex((set) => set.id === active.id);
      const newIndex = exerciseSets.findIndex((set) => set.id === over.id);

      updatePositionMutation.mutate({
        setId: active.id,
        newPosition: newIndex,
      });
    }
  };

  const handleCreateWeightedSet = (e) => {
    e.preventDefault();

    let repsToUse = newSetReps;
    let weightToUse = newSetWeight;

    if (
      (!repsToUse || !weightToUse) &&
      exerciseSets &&
      exerciseSets.length > 0
    ) {
      const lastSet = exerciseSets[exerciseSets.length - 1];
      if (!repsToUse && lastSet.reps) {
        repsToUse = lastSet.reps;
      }
      if (!weightToUse && lastSet.weight) {
        weightToUse = lastSet.weight;
      }
    }

    const parsedReps = parseInt(repsToUse, 10);
    const parsedWeight = parseFloat(weightToUse);

    if (!isNaN(parsedReps) && !isNaN(parsedWeight)) {
      createSetMutation.mutate(
        {
          exerciseId,
          reps: parsedReps,
          weight: parsedWeight,
        },
        {
          onSuccess: () => {
            setNewSetReps("");
            setNewSetWeight("");
          },
        }
      );
    } else {
      console.warn("Cannot add set: Invalid reps or weight.");
    }
  };

  const handleCreateTimedSet = (e) => {
    e.preventDefault();

    let durationToUse = newDuration;

    if (!durationToUse && exerciseSets && exerciseSets.length > 0) {
      const lastSet = exerciseSets[exerciseSets.length - 1];
      if (lastSet.duration) {
        durationToUse = lastSet.duration;
      }
    }

    const parsedDuration = parseInt(durationToUse, 10);

    if (!isNaN(parsedDuration)) {
      createSetMutation.mutate(
        {
          exerciseId,
          duration: parsedDuration,
        },
        {
          onSuccess: () => {
            setNewDuration("");
          },
        }
      );
    } else {
      console.warn("Cannot add set: Invalid duration.");
    }
  };

  if (isLoadingSets || isLoadingType) return <div>Loading sets...</div>;
  if (isErrorSets || isErrorType) return <div>Error loading exercise data</div>;

  return (
    <div className="table-responsive">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <table className="table table-sm">
          <thead>
            <tr>
              <th width="40"></th>
              {isTimedExercise ? (
                <th colSpan="2">Duration (seconds)</th>
              ) : (
                <>
                  <th>Reps</th>
                  <th>Weight</th>
                </>
              )}
              <th width="80">Actions</th>
            </tr>
          </thead>
          <SortableContext
            items={exerciseSets?.map((set) => set.id) || []}
            strategy={verticalListSortingStrategy}
          >
            <tbody>
              {exerciseSets?.map((set) =>
                isTimedExercise ? (
                  <SortableTimedSetRow
                    key={set.id}
                    id={set.id}
                    setData={set}
                    exerciseId={exerciseId}
                  />
                ) : (
                  <SortableWeightedSetRow
                    key={set.id}
                    id={set.id}
                    setData={set}
                    exerciseId={exerciseId}
                  />
                )
              )}
            </tbody>
          </SortableContext>
          <tbody>
            {isTimedExercise ? (
              <tr className="new-set-row">
                <td className="d-flex align-items-center">
                  <div className="set-drag-handle-disabled">
                    <i className="bi bi-grip-vertical text-secondary"></i>
                  </div>
                </td>
                <td colSpan="2">
                  <div className="duration-input-container">
                    <input
                      type="number"
                      min="1"
                      placeholder="Duration (seconds)"
                      className="form-control form-control-sm"
                      value={newDuration}
                      onChange={(e) => setNewDuration(e.target.value)}
                    />
                  </div>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={handleCreateTimedSet}
                  >
                    Add
                  </button>
                </td>
              </tr>
            ) : (
              <tr className="new-set-row">
                <td className="d-flex align-items-center">
                  <div className="set-drag-handle-disabled">
                    <i className="bi bi-grip-vertical text-secondary"></i>
                  </div>
                </td>
                <td>
                  <div className="reps-input-container">
                    <input
                      type="number"
                      min="1"
                      placeholder="Reps"
                      className="form-control form-control-sm"
                      value={newSetReps}
                      onChange={(e) => setNewSetReps(e.target.value)}
                    />
                  </div>
                </td>
                <td>
                  <div className="weight-input-container">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="Weight"
                      className="form-control form-control-sm"
                      value={newSetWeight}
                      onChange={(e) => setNewSetWeight(e.target.value)}
                    />
                  </div>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={handleCreateWeightedSet}
                  >
                    Add
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </DndContext>
    </div>
  );
};

export default ExerciseSets;
